<template>
  <Metric
    ref="metric"
    :embedded="embedded === 'true'"
    :titleText="titleText"
    :defaultTitleText="_getDefaultTitle() || defaultTitleText"
    :smartUrlPrefix="smartUrlPrefix"
    :filterQuery="filterQuery"
  >
    {{ result }}
  </Metric>
</template>
<script>
import { mapState } from 'vuex'
import { find, isEmpty, some, isNil } from 'lodash'
import ChartDataService from '@/services/ChartDataService'
import Metric from '@/components/Metric'
import ChartMixin from '@/mixins/ChartMixin'
import NumberFormatMixin from '@/mixins/NumberFormatMixin'

export default {
  mixins: [ChartMixin, NumberFormatMixin],
  components: {
    Metric,
  },
  props: {
    listId: String,
    measures: Array,
    titleText: String,
    defaultTitleText: String,
    embedded: String,
    smartUrlPrefix: String,
    filterQuery: String,
    calculated: Boolean,
  },
  data() {
    return {
      tileData: { type: Object, default: () => ({}) },
      title: this.measure,
    }
  },
  computed: {
    result() {
      if (this.tileData.series && this.tileData.series.length > 0) {
        const value = this.tileData.series[0].data[0]
        return this.chartNumberFormatter(value)
      }
      return this.chartNumberFormatter(this.tileData.result)
    },
    ...mapState({
      allMeasures: (state) => state.ChartCategory.measures,
      storedTitleText: (state) => state.ChartCategory.titleText,
    }),
  },
  methods: {
    chartContainsData() {
      if (some(this.tileData, isEmpty)) {
        this.$store.dispatch('setPlaceHolderMessage', this.$t('message.nodata'))
      }
      return true
    },
    _getDefaultTitle() {
      const foundMeasure = find(
        this.allMeasures,
        (measure) => measure.attributePath === this.tileData.attributePath,
      )
      if (foundMeasure) {
        const measureName = this.$t(foundMeasure.name)
        if (this.tileData.measureFunction === 'count') {
          return measureName
        }
        return `${measureName} (${this.tileData.measureFunction})`
      }
      return ''
    },
    async loadNumberTileData(listId, measures, searchAndFilterParams) {
      try {
        this.$store.dispatch('setPlaceHolderMessage', '')
        this.tileData = (
          await ChartDataService.getNumberTileData(listId, measures, searchAndFilterParams)
        ).data
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        const content = window.parent
        content.postMessage('vue-loaded', window.location.origin)
      }
    },
    onChartVisible() {
      this.$refs.metric.scaleContent()
      const defaultTitle = this._getDefaultTitle() || this.defaultTitleText
      this.setTitle(defaultTitle)
    },
    setTitle(title) {
      let newTitleText = this.titleText
      if (!newTitleText) {
        newTitleText = ''
      }
      this.$store.dispatch('setDefaultTitle', title)
      const route = {
        name: this.$route.name,
        query: {
          ...this.$route.query,
          defaultTitleText: title,
          titleText: this.embedded === 'true' ? newTitleText : this.storedTitleText,
        },
      }
      this.$router.push(route).catch(() => {})
    },
    loadData() {
      if (this.calculated) {
        this.loadCalculatedNumberTileData(this.listId, this.getJsonObject(this.filterQuery))
      } else {
        this.loadNumberTileData(this.listId, this.measures, this.getJsonObject(this.filterQuery))
      }
    },
    emitLoaded() {
      if (!isEmpty(this.tileData.placeHolderMessage)) {
        this.$store.dispatch('setPlaceHolderMessage', this.tileData.placeHolderMessage)
      }
      this.$emit('loaded', !isNil(this.result) && isEmpty(this.tileData.placeHolderMessage))
      this.isFullyRendered = true
    },
    async loadCalculatedNumberTileData(listId, searchAndFilterParams) {
      try {
        this.$store.dispatch('setPlaceHolderMessage', '')
        this.tileData = (
          await ChartDataService.getChartData(
            listId,
            this.category,
            this.dateFormat,
            this.level,
            this.measures,
            this.calculatedMeasures,
            this.series,
            this.serieslevel,
            searchAndFilterParams,
            false,
            true,
          )
        ).data
        this.setTitle(this.tileData.series[0].name)
      } catch (error) {
        const message = error.response ? error.response.data : error
        const notification = { type: 'error', message }
        await this.$store.dispatch('Notification/add', notification, { root: true })
      } finally {
        const content = window.parent
        content.postMessage('vue-loaded', window.location.origin)
      }
    },
  },
}
</script>
